import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, IMAGES, SITE } from "../content/site";
import { getLenis } from "./SmoothScroll";

export function Logo({ testId = "nav-logo-link", compact = false }) {
  return (
    <Link to="/" className="group flex items-center gap-3" data-testid={testId} aria-label="GNP Church — God's Name Proclaimed, home">
      <img src={IMAGES.logo} alt="" width="44" height="44" className="h-10 w-10 object-cover sm:h-11 sm:w-11" />
      <span className="flex flex-col leading-none">
        <span className="font-display text-xl font-bold tracking-[0.08em] text-[#F6F2F0]">GNP</span>
        {!compact && (
          <span className="mt-1 font-display text-[10px] font-medium tracking-[0.28em] text-[#B9AFB2] transition-colors group-hover:text-[#F6F2F0]">
            GOD'S NAME PROCLAIMED
          </span>
        )}
      </span>
    </Link>
  );
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const toggleRef = useRef(null);
  const firstLinkRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const lenis = getLenis();
    if (open) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
      firstLinkRef.current?.focus();
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    const onKey = (e) => { if (e.key === "Escape" && open) { setOpen(false); toggleRef.current?.focus(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const solid = scrolled || open || pathname !== "/";

  return (
    <>
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        solid ? "border-b border-[#F6F2F0]/10 bg-[#090909]/80 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-header"
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:bg-[#F6F2F0] focus:px-4 focus:py-2 focus:text-[#090909]">
        Skip to content
      </a>
      <nav className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12" aria-label="Main">
        <Logo />
        <ul className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative py-2 font-display text-[13px] uppercase tracking-[0.2em] transition-colors ${
                    isActive ? "text-[#F6F2F0] after:absolute after:inset-x-0 after:-bottom-0.5 after:h-[2px] after:bg-[#B8324E]" : "text-[#B9AFB2] hover:text-[#F6F2F0]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <Link
            to="/visit"
            data-testid="nav-plan-visit-button"
            className="hidden min-h-[44px] items-center bg-[#781C31] px-5 font-display text-[13px] font-medium uppercase tracking-[0.2em] text-[#F6F2F0] transition-colors hover:bg-[#B8324E] sm:inline-flex"
          >
            Plan Your Visit
          </Link>
          <button
            ref={toggleRef}
            type="button"
            className="inline-flex h-12 w-12 items-center justify-center text-[#F6F2F0] lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>
    </header>
      <AnimatePresence>
        {open && <MobileMenu firstLinkRef={firstLinkRef} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

function MobileMenu({ firstLinkRef, onClose }) {
  return (
    <motion.div
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
      className="fixed inset-x-0 bottom-0 top-[72px] z-40 flex flex-col justify-between overflow-y-auto bg-[#090909] px-5 pb-10 pt-6 sm:px-8 lg:hidden"
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{ clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.55, ease: [0.7, 0, 0.2, 1] }}
      data-testid="mobile-menu"
    >
      <ul className="flex flex-col">
        {NAV.map((n, i) => (
          <motion.li key={n.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }} className="border-b border-[#F6F2F0]/10">
            <NavLink
              ref={i === 0 ? firstLinkRef : undefined}
              to={n.to}
              end={n.to === "/"}
              onClick={onClose}
              data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-baseline justify-between py-4 font-display text-4xl font-semibold uppercase tracking-tight ${isActive ? "text-[#B8324E]" : "text-[#F6F2F0]"}`
              }
            >
              {n.label}
              <span className="font-display text-xs tracking-[0.3em] text-[#B9AFB2]">0{i + 1}</span>
            </NavLink>
          </motion.li>
        ))}
      </ul>
      <div className="mt-10 space-y-4">
        <Link to="/visit" onClick={onClose} data-testid="mobile-plan-visit-button" className="flex min-h-[56px] items-center justify-center bg-[#781C31] font-display text-sm uppercase tracking-[0.2em] text-[#F6F2F0]">
          Plan Your Visit
        </Link>
        <p className="text-sm text-[#B9AFB2]">
          {SITE.sunday.day}s · {SITE.sunday.time} — {SITE.address.street}, {SITE.address.city}
        </p>
      </div>
    </motion.div>
  );
}
